import { countBy, groupBy, keyBy, sortBy } from 'lodash'

export type LootContributor = {
  characterReference: wowaudit.CharacterReference
  member?: wowaudit.TeamMembership
  count: number
}

const lootGroupKeyFor = (
  character: wowaudit.CharacterReference,
  uidToUserId: Record<string, number>,
) => {
  const userId =
    character.userId ??
    (character.userAchievementUid
      ? uidToUserId[character.userAchievementUid]
      : undefined)

  if (userId != null) {
    return `user:${userId}`
  } else if (character.userAchievementUid) {
    return `uid:${character.userAchievementUid}`
  } else {
    return `character:${character.id}`
  }
}

export const buildLootGroups = ({
  members,
  formerMemberCharacters,
  filteredItems,
}: {
  members: wowaudit.TeamMembership[]
  formerMemberCharacters: wowaudit.CharacterReference[]
  filteredItems: wowaudit.LootHistoryItem[]
}) => {
  const uidToUserId: Record<string, number> = {}
  for (const reference of [
    ...members.map((member) => member.characterReference),
    ...formerMemberCharacters,
  ]) {
    if (reference.userId != null && reference.userAchievementUid) {
      uidToUserId[reference.userAchievementUid] = reference.userId
    }
  }

  const memberByCharacterId = keyBy(
    members,
    (member) => member.characterReference.id,
  )
  const formerById = keyBy(formerMemberCharacters, (character) => character.id)

  const groupKeyForCharacterId = (characterId: number) => {
    const reference =
      memberByCharacterId[characterId]?.characterReference ??
      formerById[characterId]

    if (!reference) {
      return `character:${characterId}`
    }

    return lootGroupKeyFor(reference, uidToUserId)
  }

  const groupKeyByMembershipId: Record<number, string> = {}
  for (const member of members) {
    groupKeyByMembershipId[member.id] = lootGroupKeyFor(
      member.characterReference,
      uidToUserId,
    )
  }

  const membersByGroupKey = groupBy(
    members,
    (member) => groupKeyByMembershipId[member.id],
  )

  const rows: wowaudit.TeamMembership[] = []
  for (const groupMembers of Object.values(membersByGroupKey)) {
    const visibleMembers = groupMembers.filter(
      (member) => member.teamRank?.wishlist_visibility,
    )
    if (visibleMembers.length === 0) {
      continue
    }

    const representative = sortBy(visibleMembers, [
      (member) => (member.teamRank.player_type === 'alt' ? 1 : 0),
      (member) => member.characterReference.name,
    ])[0]
    rows.push(representative)
  }

  const awardsByGroupKey = groupBy(filteredItems, (item) =>
    groupKeyForCharacterId(item.character_id),
  )

  const contributorsByGroupKey: Record<string, LootContributor[]> = {}
  for (const row of rows) {
    const groupKey = groupKeyByMembershipId[row.id]
    const awards = awardsByGroupKey[groupKey] ?? []
    const countByCharacterId = countBy(awards, 'character_id')

    contributorsByGroupKey[groupKey] = Object.entries(countByCharacterId)
      .filter(
        ([characterId]) => Number(characterId) !== row.characterReference.id,
      )
      .flatMap(([characterId, count]) => {
        const member = memberByCharacterId[Number(characterId)]
        const characterReference =
          member?.characterReference ?? formerById[Number(characterId)]

        if (!characterReference) {
          return []
        }

        return [{ characterReference, member, count }]
      })
      .sort((left, right) => right.count - left.count)
  }

  return {
    rows,
    awardsByGroupKey,
    groupKeyByMembershipId,
    contributorsByGroupKey,
  }
}
